import { Component, input, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  formatScheduleDays,
  getCurrentSchoolYear,
  getNextRegistrationDeadline,
  School,
} from '@awo-care/shared-core';
import { MatIconModule } from '@angular/material/icon';
import { SchoolDocumentDownloadComponent } from '@awo-care/shared-ui';
import { Timestamp } from 'firebase/firestore';

@Component({
  selector: 'app-school-detail',
  imports: [DatePipe, MatIconModule, SchoolDocumentDownloadComponent],
  template: `
    <div class="school-details">
      <div class="header awo-list-tile">
        <div class="awo-list-tile__avatar header-icon">
          <mat-icon aria-hidden="true">school</mat-icon>
        </div>
        <div class="awo-list-tile__body">
          <h2 class="awo-list-tile__title">{{ school().schoolName }}</h2>
          <div class="awo-list-tile__subline">
            {{ school().address.zipCode }} {{ school().address.city }}
          </div>
        </div>
      </div>

      <div class="content">
        <div class="info-list">
          <div class="info-row">
            <span class="info-row__label" i18n="@@schools.school-detail.address">Adresse</span>
            <a
              [href]="getGoogleMapsUrl(school())"
              target="_blank"
              rel="noopener noreferrer"
              class="info-row__value address-link"
            >
              {{ school().address.street }}<br />
              {{ school().address.zipCode }} {{ school().address.city }}
            </a>
          </div>

          <div class="info-row">
            <span class="info-row__label" i18n="@@schools.school-detail.contactPerson">Ansprechpartner</span>
            <span class="info-row__value">{{ school().contactFirstName }} {{ school().contactLastName }}</span>
          </div>

          <div class="info-row">
            <span class="info-row__label" i18n="@@common.email">E-Mail</span>
            <a href="mailto:{{ school().contactEmail }}" class="info-row__value email-link">
              {{ school().contactEmail || '' }}
            </a>
          </div>

          <div class="info-row">
            <span class="info-row__label" i18n="@@schools.school-detail.phone">Telefon</span>
            <a href="tel:{{ school().contactPhone }}" class="info-row__value link">
              {{ school().contactPhone || '' }}
            </a>
          </div>

          @if (school().contactMobile) {
          <div class="info-row">
            <span class="info-row__label" i18n="@@schools.school-detail.mobile">Mobil</span>
            <a href="tel:{{ school().contactMobile }}" class="info-row__value link">
              {{ school().contactMobile }}
            </a>
          </div>
          }

          @if (school().hasLunch) {
          <div class="info-row">
            <span class="info-row__label" i18n="@@schools.school-detail.lunch">Mittagessen</span>
            <span class="info-row__value">{{ school().lunch?.description }}</span>
          </div>
          }
        </div>

        @if (school().registrationDeadline) {
        <div class="deadline-box">
          <span class="deadline-box__label" i18n="@@schools.school-detail.registrationDeadline">Anmeldefrist</span>
          <span class="deadline-box__value">{{ formatDeadline(school().registrationDeadline) | date : 'dd.MM.yyyy' }}</span>
        </div>
        }

        <lib-school-document-download [school]="school()" />

        @if (school().modules?.length) {
        <div class="modules-section">
          <h3 class="awo-section-label" i18n="@@schools.school-detail.careTimes">Betreuungszeiten</h3>

          @for (module of school().modules; track module.id) {
          <div class="module-item">
            <h4>{{ module.description }}</h4>
            <div class="schedule">
              <mat-icon aria-hidden="true">schedule</mat-icon>
              <span>
                {{ formatScheduleDays(module.schedule.days || '') }}:
                {{ module.schedule.startTime }} -
                {{ module.schedule.endTime }} Uhr
              </span>
            </div>
          </div>
          }
        </div>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      /*
       * Fallback styling for the .awo-* primitive classes used above.
       *
       * The primitives (.awo-list-tile / .awo-section-label) are normally
       * defined globally in customer-ui's _awo-primitives.scss and driven by
       * the --awo-* custom properties from its :root. This component is also
       * rendered standalone by the website app (via app-school-list's popover
       * on the /schools route) — an app that does NOT load customer-ui's
       * primitives stylesheet or define any --awo-* variables.
       *
       * These rules (scoped to this component only by Angular's emulated
       * view encapsulation — they cannot leak into other components) provide
       * the same visual recipe with graceful var() fallbacks, so the school
       * detail popup still looks right on website instead of rendering as
       * unstyled markup. On customer-ui the --awo-* variables are always
       * defined, so the resolved values are identical to using the global
       * primitives directly; only the fallback literals are ever used
       * elsewhere. Keep these values in sync with
       * apps/customer-ui/src/styles/_awo-primitives.scss.
       */
      .awo-section-label {
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-size: 0.75rem;
        font-weight: 700;
        color: var(--awo-text-subtle, #6f6a66);
        margin: 0 0 0.75rem;
      }

      .awo-list-tile {
        display: flex;
        align-items: center;
        gap: 1rem;

        &__avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--awo-red-tint, #fdeaec);
          color: var(--theme-primary, #e2001a);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.9rem;
          flex: 0 0 auto;
        }

        &__body {
          flex: 1 1 auto;
          min-width: 0;
        }

        &__title {
          font-weight: 700;
          color: var(--awo-text, #1c1b1a);
        }

        &__subline {
          color: var(--awo-text-subtle, #6f6a66);
          font-size: 0.9rem;
          margin-top: 0.15rem;
        }

        &__chevron {
          color: var(--awo-text-subtle, #6f6a66);
          flex: 0 0 auto;
        }
      }

      .school-details {
        background: var(--awo-card, #ffffff);
        border-radius: var(--awo-radius-lg, 16px);
        overflow: hidden;
        width: 100%;
      }

      .header {
        padding: 1.5rem 2.75rem 1.25rem 1.5rem;
        border-bottom: 1px solid var(--awo-border, #eae6e2);
      }

      .header-icon {
        width: 44px;
        height: 44px;

        mat-icon {
          font-size: 22px;
          width: 22px;
          height: 22px;
        }
      }

      .header h2 {
        margin: 0;
        font-size: clamp(15px, 3.5vw, 18px);
        line-height: 1.3;
        word-break: break-word;
        hyphens: auto;
      }

      @media (max-width: 480px) {
        .header {
          padding: 1rem 2.5rem 1rem 1rem;
        }

        .content {
          padding: 1rem;
        }
      }

      .content {
        padding: clamp(1rem, 3vw, 1.5rem);
      }

      .info-list {
        display: flex;
        flex-direction: column;
      }

      .info-row {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 1rem;
        padding: 0.7rem 0;
        border-bottom: 1px solid var(--awo-border, #eae6e2);

        &:last-child {
          border-bottom: none;
        }
      }

      .info-row__label {
        flex: 0 0 auto;
        color: var(--awo-text-subtle, #6f6a66);
        font-size: clamp(12px, 2.5vw, 13px);
        font-weight: 500;
      }

      .info-row__value {
        flex: 1 1 auto;
        min-width: 0;
        text-align: right;
        color: var(--awo-text, #1c1b1a);
        font-size: clamp(14px, 3vw, 15px);
        font-weight: 500;
        line-height: 1.4;
        word-break: break-word;
        overflow-wrap: anywhere;
      }

      a.info-row__value {
        text-decoration: none;
        cursor: pointer;

        &:hover {
          color: var(--theme-primary);
        }
      }

      .email-link {
        color: var(--theme-primary);
      }

      .deadline-box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        background: var(--awo-chip-warn-bg, #fff7e8);
        border-radius: var(--awo-radius-sm, 8px);
        padding: 0.75rem 1rem;
        margin: 1rem 0;
      }

      .deadline-box__label {
        color: var(--awo-chip-warn-text, #9a6a00);
        font-size: clamp(12px, 2.5vw, 13px);
        font-weight: 600;
      }

      .deadline-box__value {
        color: var(--awo-chip-warn-text, #9a6a00);
        font-size: clamp(14px, 3vw, 15px);
        font-weight: 700;
      }

      .modules-section {
        margin-top: 1.25rem;
        padding-top: 1rem;
        border-top: 1px solid var(--awo-border, #eae6e2);

        h3 {
          margin: 0 0 1rem;
        }
      }

      .module-item {
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }

        h4 {
          font-size: clamp(14px, 3.5vw, 16px);
          font-weight: 500;
          margin: 0 0 0.5rem;
          color: var(--awo-text, #1c1b1a);
          word-break: break-word;
          line-height: 1.3;
        }

        .schedule {
          display: flex;
          align-items: flex-start;
          gap: clamp(0.25rem, 1.5vw, 0.5rem);
          color: var(--awo-text-muted, #6b6560);

          mat-icon {
            flex-shrink: 0;
            font-size: clamp(16px, 3vw, 18px);
            width: clamp(16px, 3vw, 18px);
            height: clamp(16px, 3vw, 18px);
            margin-top: 0.1rem;
            color: var(--awo-text-subtle, #6f6a66);
          }

          span {
            font-size: clamp(13px, 2.5vw, 14px);
            line-height: 1.4;
            word-break: break-word;
          }
        }
      }

      /*
       * Scoped override for the SHARED lib-school-document-download component.
       * Only applies when an ancestor carries .awo-redesign (customer-ui app root).
       * employee-ui does not set that class, so its default styles are untouched there.
       */
      :host-context(.awo-redesign) {
        ::ng-deep lib-school-document-download {
          .documents-card {
            background: var(--awo-card);
            border: 1px solid var(--awo-border);
            border-radius: var(--awo-radius-md);
            box-shadow: none;
            margin: 1rem 0 0;
          }

          .file-icon {
            color: var(--theme-primary);
          }

          .filesize {
            color: var(--awo-text-subtle);
          }

          .document-item {
            border-bottom-color: var(--awo-border);
          }

          .download-btn {
            border-color: var(--theme-primary);
            color: var(--theme-primary);
            background: transparent;
          }
        }
      }
    `,
  ],
})
export class SchoolDetailComponent {
  school = input.required<School>();
  formatScheduleDays = formatScheduleDays;

  /**
   * Nächste anstehende Anmeldefrist. Tag und Monat stehen in der Schule, das
   * Jahr ergibt sich aus dem Schuljahr — sonst zeigt die öffentliche Seite eine
   * längst verstrichene Frist (#746).
   */
  formatDeadline(timestamp: Timestamp | undefined): Date | null {
    return getNextRegistrationDeadline(
      timestamp,
      getCurrentSchoolYear().year,
    );
  }

  getGoogleMapsUrl(school: School): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      school.schoolName
    )}+${encodeURIComponent(school.address.city)}`;
  }
}
